import React, { useState } from 'react';
import { EmpButton as Button } from '@empowernow/ui';
import { CrudForm } from './CrudForm';
import type { CrudActionProps } from './types';

function humanizeWorkflowName(name: string): string {
	return name
		.split('_')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
}

function Modal({ isOpen, onClose, title, children }: {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	children: React.ReactNode;
}) {
	if (!isOpen) return null;
	
	return (
		<div style={{
			position: 'fixed',
			inset: 0,
			backgroundColor: 'rgba(0, 0, 0, 0.6)',
			backdropFilter: 'blur(6px)',
			WebkitBackdropFilter: 'blur(6px)',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			zIndex: 1000,
			padding: '20px',
		}}>
			<div style={{
				width: '100%',
				maxWidth: '600px',
				maxHeight: '90vh',
				overflow: 'auto',
				backgroundColor: 'var(--modal-bg, var(--bg-color, #0d1526))',
				border: '1px solid var(--card-border, var(--border-color, rgba(255,255,255,0.12)))',
				borderRadius: '12px',
				padding: '24px',
				boxShadow: '0 24px 48px rgba(0,0,0,0.5), 0 0 1px rgba(0,231,246,0.2)',
			}}>
				<div style={{ 
					display: 'flex', 
					justifyContent: 'space-between', 
					alignItems: 'center',
					marginBottom: '20px'
				}}>
					<h2 style={{ margin: 0, color: 'var(--main-text)' }}>{title}</h2>
					<button
						onClick={onClose}
						style={{ 
							background: 'transparent',
							border: 'none',
							color: 'var(--main-text-secondary)',
							padding: '8px',
							fontSize: '24px',
							cursor: 'pointer',
							lineHeight: 1
						}}
					>
						×
					</button>
				</div>
				{children}
			</div>
		</div>
	);
}

export function CrudAction({
	sdk,
	label,
	workflowName,
	run,
	data = {},
	formId,
	variant = 'primary',
	disabled = false,
	onSuccess,
	onError,
	confirm
}: CrudActionProps) {
	const [showModal, setShowModal] = useState(false);
	const [loading, setLoading] = useState(false);
	
	const workflow = run || workflowName;
	
	if (!workflow) {
		throw new Error('CrudAction requires either "run" or "workflowName" prop');
	}
	
	const autoLabel = label || humanizeWorkflowName(workflow);
	
	const handleClick = async () => {
		if (confirm) {
			const message = typeof confirm === 'string' 
				? confirm 
				: `Are you sure you want to ${autoLabel.toLowerCase()}?`;
			if (!window.confirm(message)) {
				return;
			}
		}
		
		if (formId) {
			setShowModal(true);
		} else {
			setLoading(true);
			try {
				const result = await sdk.crud.executeWorkflow(workflow, data);
				
				if (result?.status === 'COMPLETED') {
					onSuccess?.(result?.result);
				} else if (result?.status === 'ERROR') {
					onError?.(new Error(result?.message || 'Workflow failed'));
				} else if (result?.result?.form) {
					setShowModal(true);
				}
			} catch (err) {
				onError?.(err instanceof Error ? err : new Error('Workflow failed'));
			} finally {
				setLoading(false);
			}
		}
	};
	
	const buttonStyle = {
		background: variant === 'primary' ? 'var(--selected-bg)' : 
					variant === 'danger' ? 'rgba(239,68,68,0.9)' : 
					'var(--input-bg)',
		border: variant === 'primary' ? 'none' :
				variant === 'danger' ? '1px solid rgba(239,68,68,1)' :
				'1px solid var(--border-color)',
		color: variant === 'primary' ? 'var(--selected-text)' :
			   variant === 'danger' ? '#fff' :
			   'var(--main-text)',
		opacity: loading || disabled ? 0.7 : 1,
		cursor: loading || disabled ? 'not-allowed' : 'pointer',
		padding: '8px 16px',
		borderRadius: '6px',
		fontSize: '14px',
		fontWeight: 500
	};
	
	return (
		<>
			<button
				onClick={handleClick}
				disabled={disabled || loading}
				style={buttonStyle}
			>
				{loading ? 'Loading...' : autoLabel}
			</button>
			
			{showModal && (
				<Modal
					isOpen={showModal}
					onClose={() => setShowModal(false)}
					title={autoLabel}
				>
					<CrudForm
						sdk={sdk}
						workflowName={workflow}
						initialData={data}
						onSuccess={(result) => {
							setShowModal(false);
							onSuccess?.(result);
						}}
						onCancel={() => setShowModal(false)}
						onError={onError}
					/>
				</Modal>
			)}
		</>
	);
}

